import * as React from 'react';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import { Checkbox, Divider, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, Radio, RadioGroup, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import UIScenarioRow from './UIScenarioRow';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { appdata } from '../internalModules';


type Props = {
  index: number
  scenarioId: string
  deleteScenario: Function
  updateScenarios: Function
}

export default function UIScenarioCard({index, scenarioId, deleteScenario, updateScenarios}: Props) {
  
  const scenario = appdata.getScenario(scenarioId);

  const forceUpdate: () => void = React.useState({})[1].bind(null, {});

  //layout grid columns
  const c1 = 6;
  const c2 = 3;
  const c3 = 3;

  const addRow = () => {
    scenario.addRow();
    forceUpdate();
  }

  const removeRow = () => {
    scenario.removeLastRow();
    updateScenarios()
  }

  const handleDeleteScenario = () => {
    deleteScenario(scenarioId);
    updateScenarios()
  }

  const handleBaselineChange = () => {
    appdata.setScenarioAsBaseline(scenarioId)
    updateScenarios()
  }

  const handleChangeCalcMethod = (event: React.ChangeEvent<HTMLInputElement> ) => {
    scenario.setCalcMethod(event.target.value);
    updateScenarios()
  }

  const handleChangeTotalArea = (event: React.ChangeEvent<HTMLInputElement> ) => {
    scenario.setTotalArea( Math.abs(parseInt(event.target.value)) );
    updateScenarios()
  }

  const changeRowWallType = (rowIndex: number, wTypeId: string) => {
    scenario.setRowWallTypeId(rowIndex, wTypeId);
    updateScenarios()
  }

  const changeRowAmount = (rowIndex: number, rowAmount: number) => {
    scenario.setRowAmount(rowIndex, rowAmount);
    updateScenarios()
  }

  const cardHeader = () => (
    <Grid container >
      <Grid item xs={10}>
        <Typography variant='h4'>{`Scenario ${index + 1}`}</Typography>
      </Grid>
      <Grid item xs={2} display='flex' justifyContent={'right'}>
        <Tooltip title='Delete scenario' placement='top' arrow>
          <IconButton
            size='small'
            sx={{borderRadius: 0}}
            onClick={handleDeleteScenario}
          >
            <ClearIcon/>
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  )
  
  const listHeader = () => (
    <>
      <Tooltip title='Set as baseline scenario. Delta graph will compare other scenarios to the baseline.' placement='bottom' arrow>
        <FormControlLabel control={
          <Checkbox
            checked={scenario.isBaseline}
            onChange={handleBaselineChange}
          />
        } label="set as baseline" />
      </Tooltip>
      <Grid container alignItems={'center'}>
        <Grid item xs={c1}>
          <Typography className='h5'>Assembly</Typography>
        </Grid>
        <Grid item xs={c2} display='flex' justifyContent={'space-evenly'}>
          <Tooltip title='Input method for this scenario: By area or by percent of a total area.' placement='top' arrow>
            <RadioGroup
              row
              value={scenario.calcMethod}
              onChange={handleChangeCalcMethod}
            >
              <FormControlLabel
                value="sqft"
                control={<Radio size='small'/>}
                label="sqft" />
              <FormControlLabel
                value="%"
                control={<Radio size='small'/>}
                label="%" />
            </RadioGroup>
          </Tooltip>
        </Grid>
        <Grid item xs={c3} display='flex' justifyContent={'right'} >
          <Typography className='h5'>MTCO2e</Typography>
        </Grid>
      </Grid>
    </>
    
  )

  const listRows = () => (
    <List>
      {scenario.rows.map( (row, index) => (
        <UIScenarioRow
          scenarioId={scenario.guid}
          rowIndex={index}
          amountDisabled={index === scenario.rows.length-1 && scenario.calcMethod === '%'}
          key={scenario.guid + index.toString()}
          changeRowWallType={changeRowWallType}
          changeRowAmount={changeRowAmount}
        />
      ))}
    </List>
  )

  const footer = () => (
    <Grid container alignItems={'center'}>
      <Grid item xs={c1}>
        <Tooltip title='Add a new row to the scenario.' placement='bottom' arrow>
          <IconButton
            sx={{borderRadius: 0}}
            onClick={addRow}
          >
            <AddCircleOutlineIcon/>
          </IconButton>
        </Tooltip>
        <Tooltip title='Remove the last row from the scenario.' placement='bottom' arrow>
          <IconButton
            sx={{borderRadius: 0}}
            onClick={removeRow}
          >
            <RemoveCircleOutlineIcon/>
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item xs={c2} display='flex'>
        <FormControl 
          variant="filled" 
          fullWidth
        >
            <TextField
              value={scenario.totalArea}
              type='number'
              size='small'
              onChange={handleChangeTotalArea}
              disabled={scenario.calcMethod === 'sqft'}
              InputProps={{
                endAdornment: <InputAdornment position="end">Total SF</InputAdornment>
              }}
            />
        </FormControl>
      </Grid>
      <Grid item xs={c3} display='flex' justifyContent={'right'} >
        <Stack direction='column' spacing={1} textAlign={'right'}>
          <Tooltip title='Total GWP (MTCO2e) for the scenario.' placement='top' arrow>
            <Typography
              className='h5'
            >
              {(scenario.gwp / 1000).toFixed(2)}
            </Typography>
          </Tooltip>
          <Tooltip title='GWP per total square foot of wall or horizontal assembly area (not per building area).' placement='bottom' arrow>
            <Typography
              className='h5'
              fontWeight={'bold'}
            >
              {(scenario.gwp / scenario.totalArea).toFixed(2) + ' kgCO2e/sf'}
            </Typography>
          </Tooltip>
        </Stack>
      </Grid>
    </Grid>
  )

  return (
    <Paper sx={{p:2, m:1, minWidth:550}}>
      
      <>
        {cardHeader()}
        <Divider/>
        {listHeader()}
        <Divider/>
        {listRows()}
        <Divider/>
        {footer()}
      </>
    </Paper>

  );
}
